import { Car, ChevronDown, ChevronRight, Folder } from 'lucide-react';

import type { AssetUrlLookup } from '../grid/useAssetUrls';
import type { OlaTokenLookup } from '../grid/useOlaTokens';
import { AssetTypeSection } from './AssetTypeSection';
import { UNASSIGNED_NAMEPLATE_KEY, typeCollapseKey, type NameplateGroup } from './assetHierarchy';

interface NameplateSectionProps {
  nameplateGroup: NameplateGroup;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  collapsedTypes: Set<string>;
  onToggleTypeCollapse: (collapseKey: string) => void;
  urls: AssetUrlLookup;
  olaTokens: OlaTokenLookup;
  isSelected: (id: string) => boolean;
  isSelectable?: (id: string) => boolean;
  onSelect: (id: string, modifiers: { shift?: boolean; meta?: boolean }) => void;
  onOpen: (id: string) => void;
  eligibleIds?: Set<string> | null;
  onToggleBasket?: (id: string) => void;
  basketIds?: Set<string>;
  expiredIds?: Set<string>;
  expiringSoonIds?: Set<string>;
}

export function NameplateSection({
  nameplateGroup,
  isCollapsed,
  onToggleCollapse,
  collapsedTypes,
  onToggleTypeCollapse,
  urls,
  olaTokens,
  isSelected,
  isSelectable,
  onSelect,
  onOpen,
  eligibleIds,
  onToggleBasket,
  basketIds,
  expiredIds,
  expiringSoonIds,
}: NameplateSectionProps) {
  const isUnassigned = nameplateGroup.nameplate === UNASSIGNED_NAMEPLATE_KEY;
  const sectionId = `nameplate-section-${encodeURIComponent(nameplateGroup.nameplate)}`;

  return (
    <section id={sectionId} className="flex flex-col rounded-lg border border-slate-200 bg-white">
      {/* Nameplate Header */}
      <button
        type="button"
        onClick={onToggleCollapse}
        aria-expanded={!isCollapsed}
        aria-controls={`${sectionId}-content`}
        className="flex w-full items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3.5 py-2.5 text-left transition-colors hover:bg-slate-100/80 rounded-t-lg"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${
              isUnassigned ? 'bg-slate-100 text-slate-500' : 'bg-blue-50 text-[#0014cc]'
            }`}
          >
            {isUnassigned ? <Folder className="h-3.5 w-3.5" /> : <Car className="h-3.5 w-3.5" />}
          </div>

          <h3 className="text-sm font-semibold text-slate-900 truncate">
            {nameplateGroup.displayName}
          </h3>

          <span className="rounded-full bg-slate-200/80 px-2 py-0.5 text-[11px] font-medium text-slate-600">
            {nameplateGroup.totalCount} {nameplateGroup.totalCount === 1 ? 'asset' : 'assets'}
          </span>
        </div>

        <div className="flex items-center gap-1 text-slate-400">
          <span className="text-xs text-slate-500">{isCollapsed ? 'Expand' : 'Collapse'}</span>
          {isCollapsed ? (
            <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </div>
      </button>

      {/* Asset-type sections */}
      {!isCollapsed && (
        <div id={`${sectionId}-content`} className="flex flex-col gap-5 p-3.5">
          {nameplateGroup.assetTypes.map((typeGroup) => {
            const collapseKey = typeCollapseKey(nameplateGroup.nameplate, typeGroup.assetType);
            const isTypeCollapsed = collapsedTypes.has(collapseKey);

            return (
              <AssetTypeSection
                key={typeGroup.assetType}
                typeGroup={typeGroup}
                nameplateKey={nameplateGroup.nameplate}
                isCollapsed={isTypeCollapsed}
                onToggleCollapse={() => onToggleTypeCollapse(collapseKey)}
                urls={urls}
                olaTokens={olaTokens}
                isSelected={isSelected}
                isSelectable={isSelectable}
                onSelect={onSelect}
                onOpen={onOpen}
                eligibleIds={eligibleIds}
                onToggleBasket={onToggleBasket}
                basketIds={basketIds}
                expiredIds={expiredIds}
                expiringSoonIds={expiringSoonIds}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
